/**
 * Video Settings & Engine Configurations
 * Single Source of Truth for Video Generation Options.
 */

export type VideoEngineId = 'veo' | 'ltx2_fal' | 'wan' | 'kling' | 'runway' | string;
export type VideoPresetId = string;

/**
 * Start Frame Mode - Controls how the first frame / reference image is handled
 *
 * - prompt_only: No image sent to Veo API (text-to-video only)
 * - reference_soft: Image sent as reference/inspiration (default Veo behavior)
 * - lock_first_frame: GUARANTEE first video frame = Nano Pro image
 *   → If Veo can't guarantee, applies fallback:
 *     - Option A: Prepend 0.3s still segment of Nano Pro image before Veo video
 *     - Option B: Auto-switch to LTX2 i2v/keyframe mode if available
 */
export type StartFrameMode = 'prompt_only' | 'reference_soft' | 'lock_first_frame';

// Feature Flags
export const EXPERIMENTAL_TRAINING = false; // Hide Flux/LoRA until UX is ready

export interface VideoSettings {
    engine: VideoEngineId;
    preset: VideoPresetId;
    durationSec: number;
    aspectRatio: '16:9' | '9:16' | '1:1';
    withAudio: boolean;
    fps?: 24 | 25 | 30 | 50 | 60;

    /**
     * Start Frame Mode - Controls how Nano Pro image is used as first frame
     * @default 'reference_soft'
     */
    startFrameMode?: StartFrameMode;

    // Optional: For extensions (Image-to-Video with imported video context)
    externalVideo?: {
        source: 'local' | 'gdrive';
        fileName: string;
        url: string;         // DEPRECATED: Use sourceUrl. Kept for back-compat.
        sourceUrl?: string;  // Persistent HTTP URL (Supabase/Drive-Proxy) for Generation
        previewUrl?: string; // UI Only (Blob or Proxy URL)
        durationSec?: number;
        file?: File;         // For local uploads (not persisted)
    };
}

export const VIDEO_ENGINES: Record<string, {
    label: string;
    presets: { id: string; label: string; default?: boolean }[];
    description?: string;
}> = {
    veo: {
        label: "🎥 Google Veo 3.1",
        description: "Génération haute fidélité via Vertex AI",
        presets: [
            { id: "veo_hq", label: "Haute qualité (Veo 3.1)", default: true },
            { id: "veo_fast", label: "Rapide (Veo Fast)" }
        ],
    },
    ltx2_fal: {
        label: "🎞️ LTX-2 (Fal)",
        description: "Rapide & créatif via Fal.ai",
        presets: [
            { id: "ltx2_default", label: "Standard", default: true },
            { id: "ltx2_20s_50fps", label: "20s • 50fps" },
            { id: "ltx2_audio", label: "Audio-to-Video" }
        ],
    },
    wan: {
        label: "🎬 Wan 2.6 (Fal)",
        description: "Modèle open-source performant",
        presets: [
            { id: "wan_default", label: "Wan 2.1 Standard", default: true },
            { id: "wan_1.3b", label: "Wan 2.6 Léger (1.3B)" },
            { id: "wan_14b", label: "Wan 2.6 Pro (14B)" }
        ]
    },
    kling: {
        label: "🎥 Kling 1.6 (Replicate)",
        description: "Excellent rendu de mouvement",
        presets: [
            { id: "kling_pro", label: "Kling Pro", default: true },
            { id: "kling_standard", label: "Kling Standard" }
        ]
    }
};

/**
 * Ensures VideoSettings are always valid.
 * - If engine is unknown, keeps it (fallback).
 * - If preset doesn't belong to engine, forces default preset for that engine.
 */
export function normalizeVideoSettings(s: VideoSettings): VideoSettings {
    const engineConfig = VIDEO_ENGINES[s.engine];

    // If engine unknown, fallback to DEFAULT (as requested)
    if (!engineConfig) {
        return { ...DEFAULT_VIDEO_SETTINGS };
    }

    const isPresetValid = engineConfig.presets.some(p => p.id === s.preset);

    if (isPresetValid) {
        return s;
    }

    // Invalid preset? Use the one marked 'default: true', or the first one.
    const defaultPreset = engineConfig.presets.find(p => p.default) ?? engineConfig.presets[0];

    return {
        ...s,
        preset: defaultPreset?.id ?? "veo_hq" // Fallback safety
    };
}

export const DEFAULT_VIDEO_SETTINGS: VideoSettings = {
    engine: 'veo',
    preset: 'veo_hq',
    durationSec: 6,
    aspectRatio: '16:9',
    withAudio: false,
    startFrameMode: 'reference_soft', // Default: send image as reference
};

/**
 * Start Frame Mode Options for UI display
 */
export const START_FRAME_MODE_OPTIONS: Array<{
    id: StartFrameMode;
    label: string;
    description: string;
    icon: string;
}> = [
    {
        id: 'prompt_only',
        label: 'Prompt Only',
        description: 'No image sent - pure text-to-video',
        icon: '📝'
    },
    {
        id: 'reference_soft',
        label: 'Reference (Soft)',
        description: 'Image as inspiration - Veo may vary the first frame',
        icon: '🖼️'
    },
    {
        id: 'lock_first_frame',
        label: 'Lock First Frame',
        description: 'GUARANTEE: Video starts with exact Nano Pro image',
        icon: '🔒'
    }
];
